import React, { useEffect } from 'react';
import s from './UserNotifications.module.scss';
import ProfileMenuHeader from 'modules/app/components/ProfileMenu/ProfileMenuHeader';
import { useNavigate } from 'react-router-dom';
import Page from 'modules/app/components/Page/Page';
import { useUserNotifications } from 'sdk/hooks/notifications/useUserNotifications';
import { format, isToday, isYesterday } from 'date-fns';
import useUser from 'sdk/hooks/user/useUser';
import { ReactComponent as NoDataIcon } from '../../../images/icons/no-data.svg';
import { useActions } from 'store/useActions';
import { useTranslation } from 'react-i18next';

import { ReactComponent as RiyalIcon } from 'images/new-icons/riyal.svg';
import VoucherIcon from './voucher-icon.svg';


const UserNotifications = () => {
  const { t } = useTranslation() as any;
  const navigate = useNavigate();
  const { data: user } = useUser();
  const { data: notifications } = useUserNotifications(user?.user?.id);
  const { setIsButtonMenuVisible } = useActions();

  const groupNotifications = () => {
    const groups: Record<string, any[]> = {
      today: [],
      yesterday: [],
      older: [],
    };

    notifications?.forEach(notification => {
      const date = new Date(notification.created_at);
      if (isToday(date)) {
        groups.today.push(notification);
      } else if (isYesterday(date)) {
        groups.yesterday.push(notification);
      } else {
        groups.older.push(notification);
      }
    });

    return groups;
  };

  const renderNotificationDate = (notification: any) => {
    const date = new Date(notification.created_at);
    if (isToday(date)) {
      return 'Today';
    } else if (isYesterday(date)) {
      return 'Yesterday';
    } else {
      return format(date, 'MMM d');
    }
  };

  useEffect(() => {
    setIsButtonMenuVisible(false);
  }, []);

const renderVoucherNotification = (notification: any) => (
  <div key={notification.id} className={s.notifications_product}> 
    <div className={s.notifications_product_image_wrapper}>
    <img src={VoucherIcon} alt="voucher" className={s.notifications_vaucher_image} style={{objectFit:"contain"}} />

    </div>
 
    <div className={s.notifications_product_info_wrapper}>
      <div className={s.notifications_product_info}>
     {/* <div className={s.notifications_product_info_name}>{notification.title}</div> */}
        <div className={s.notifications_product_status}>{notification.message}</div>
      </div>
    </div>
 
    <div className={s.notifications_product_date}>
      {notification.created_at && format(new Date(notification.created_at), 'MMM dd, yyyy')}
    </div>
  </div>
);

  const renderBookingNotification = (notification: any) => (
    <div key={notification.id}>
      <div className={s.notifications_salon}>
        <div className={s.notifications_salon_container}>
          <img
            src={notification.booking_transactions?.services?.image}
            className={s.notifications_salon_image}
            alt="salon"
          />
          <div className={s.notifications_salon_name}>
            {notification.booking_transactions?.services?.name}
          </div>
        </div>
        <div className={s.notifications_salon_time}>
          <div className={s.notifications_salon_time_date}>
            {format(new Date(notification.booking_transactions?.date), 'MMM dd, yyyy')}
          </div>
          <div className={s.notifications_salon_time_date}>
            {format(
              new Date(`2000-01-01T${notification.booking_transactions?.start_time}`),
              'h:mm a',
            )}
          </div>
        </div>
      </div>
      <div className={s.notifications_salon_service}>
        {notification.booking_transactions?.services?.menu_category?.name}
      </div>
      <div className={s.notifications_salon_ready}>{notification.message}</div>
    </div>
  );

  const renderProductNotification = (notification: any) => {
    const hasProduct = !!notification.products;
    const hasImage = !!notification.products?.image;

    return (
      <div key={notification.id} className={hasImage ? s.notifications_product : s.notifications_product_no_image}>
        {hasImage && (
          <img
            src={notification.products?.image}
            className={s.notifications_product_image}
            alt="product"
          />
        )}
        <div>
          <div className={s.notifications_product_info}>
            <div className={s.notifications_product_info_name}>
              {notification.products?.name || notification.title}
            </div>
            {notification.products?.price !== undefined && notification.products?.price !== null && (
              <div className={s.notifications_product_info_price}>
                <RiyalIcon className={s.riyal_icon} />
                {notification.products?.price?.toFixed(2)}
              </div>
            )}
          </div>
          {notification.products?.store_owners_profile?.store_name && (
            <div className={s.notifications_product_seller}>
              Sold by {notification.products?.store_owners_profile?.store_name}
            </div>
          )}
          {notification.products?.store_owners_profile?.full_address && (
            <div className={s.notifications_product_shipping}>
              Shipping from {notification.products?.store_owners_profile?.full_address}
            </div>
          )}

          <div className={s.notifications_product_status}>{notification.message}</div>
        </div>
      </div>
    );
  };

  const groups = groupNotifications();

  const hasNotifications = notifications && notifications.length > 0;

  return (
    <div style={{ overflow: 'auto' }}>
      <Page activeLink="/feed">
        <div className={s.notifications_header}>
          <ProfileMenuHeader
            text="Notification"
            onClose={() => {
              setIsButtonMenuVisible(true);
              navigate(-1);
            }}
          />
        </div>

        {!hasNotifications ? (
          <div className={s.notifications_noNotifications}>
            <NoDataIcon className={s.notifications_noNotifications_icon} />
            <div className={s.notifications_noNotifications_title}>{t('No data')}</div>
            <div className={s.notifications_noNotifications_text}>
              {t('Your notifications')} <br /> {t('will appear here')}
            </div>
          </div>
        ) : (
          <div className={s.notifications_container}>
            {groups.today.map(notification =>
              notification.type === 'booking'
                ? renderBookingNotification(notification)
                : notification.message === 'Your vaucher was booked!'
                ? renderVoucherNotification(notification)
                : renderProductNotification(notification),
            )}

            {groups.yesterday.length > 0 && (
              <>
                <div className={s.notifications_date}>Yesterday</div>
                {groups.yesterday.map(notification =>
                  notification.type === 'booking'
                    ? renderBookingNotification(notification)
                    : notification.message ===  'Your vaucher was booked!'
                    ? renderVoucherNotification(notification)
                    : renderProductNotification(notification),
                )}
              </>
            )}

            {groups.older.length > 0 &&
              groups.older
                .reduce((acc: any[], notification: any) => {
                  const date = renderNotificationDate(notification);
                  const existingGroup = acc.find(g => g.date === date);

                  if (existingGroup) {
                    existingGroup.notifications.push(notification);
                  } else {
                    acc.push({ date, notifications: [notification] });
                  }

                  return acc;
                }, [])
                .map(group => (
                  <React.Fragment key={group.date}>
                    <div className={s.notifications_date}>{group.date}</div>
                    {group.notifications.map(notification =>
                      notification.type === 'booking'
                        ? renderBookingNotification(notification)
                        : notification.message ===  'Your vaucher was booked!'
                        ? renderVoucherNotification(notification)
                        : renderProductNotification(notification),
                    )}
                  </React.Fragment>
                ))}
          </div>
        )}
      </Page>
    </div>
  );
};

export default UserNotifications;
